import React from 'react'

import { isUUID } from '../../utilities'
import { getUserMeta } from '../../lib/api'
import useUserPermissions from '../../hooks/permissions/useUserPermissions'

export const MetaContext = React.createContext(null)

// Giving a userID will cause meta to be loaded from that id
const MetaProvider = ({ user, children }) => {
  const { hasBasic } = useUserPermissions()

  const [meta, setMeta] = React.useState([])
  const metaCache = React.useRef({})

  const handleGetMeta = React.useCallback(
    async (id, timelineMode = false, name = undefined) => {
      if (!timelineMode && id in metaCache.current) {
        return metaCache.current[id]
      }

      try {
        const response = await getUserMeta(id)
        // If user is accessed from engagement hub and its new, create a meta to be able to edit its data.
        if (response.length === 0) {
          metaCache.current[id] = [] // [{ key: 'name', value: name }]
          // Store in cache
        } else {
          metaCache.current[id] = response.filter(x => isUUID(x.key))
        }

        return metaCache.current[id]
      } catch (err) {
        console.error('Error getting meta', err)
        throw err
      }
    },
    []
  )

  // fetching user meta on timeline load
  const fetchMeta = React.useCallback(
    async (id, shouldSet = true, timelineMode = false, name = undefined) => {
      if (!hasBasic) {
        return
      }

      if (!id) {
        return
      }

      const meta = await handleGetMeta(id, timelineMode, name).catch(err => {
        throw err
      })

      if (shouldSet) {
        //If user is accessed from engagement hub and its new, create a meta to be able to edit its data.
        if (meta.length === 0 && timelineMode) {
          setMeta([{ key: '', value: '' }])
        } else {
          setMeta(meta)
        }
      }
      return meta
    },
    [hasBasic, handleGetMeta]
  )

  // Load meta from api
  React.useEffect(() => {
    if (!user?.id) {
      return
    }

    let mounted = true

    fetchMeta(user.id, false).then(meta => mounted && setMeta(meta))

    return () => {
      mounted = false
    }
  }, [user, fetchMeta])

  // Updates cache on user or meta change
  React.useEffect(() => {
    if (!user) {
      return
    }

    if (user?.id in metaCache.current) {
      metaCache.current[user?.id] = meta
    }
  }, [meta, user])

  // Adds or updates meta value by id
  const updateMetaVal = React.useCallback((key, value, row, rowKey) => {
    setMeta(old => {
      const changed = [...old]
      let idx

      if (rowKey) {
        idx = changed.findIndex(
          obj => obj.key === key && obj.row === row && obj.row_key === rowKey
        )
      } else {
        idx = changed.findIndex(obj => obj.key === key)
      }

      if (idx >= 0) {
        changed[idx].value = value
      } else {
        changed.push({ key, value, row, row_key: rowKey })
      }

      return changed
    })
  }, [])

  return (
    <MetaContext.Provider
      value={{
        meta,
        setMeta,
        metaCache,
        fetchMeta,
        updateMetaVal
      }}
    >
      {children}
    </MetaContext.Provider>
  )
}

export default MetaProvider

export const useMetaContext = () => React.useContext(MetaContext)
